import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Image, Mic, MessageSquare, DollarSign, Video, FileText, User, Info } from "lucide-react";
import { estimateCost, formatCost } from "@/utils/costEstimation";
import { useLanguage } from "@/contexts/LanguageContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface CostEstimatorProps {
  imageCount: number;
  audioCount: number;
  videoCount?: number;
  documentCount?: number;
  contactCount?: number;
  includesSummary?: boolean;
  isAdmin?: boolean;
  showTooltips?: boolean;
}

export function CostEstimator({ 
  imageCount, 
  audioCount, 
  videoCount = 0, 
  documentCount = 0, 
  contactCount = 0, 
  includesSummary = true, 
  isAdmin = false,
  showTooltips = false 
}: CostEstimatorProps) {
  const { t } = useLanguage();
  const estimate = estimateCost(imageCount, audioCount, includesSummary, isAdmin, videoCount, documentCount, contactCount);

  if (estimate.totalCalls === 0) {
    return null;
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            {t("cost.estimation")}
          </h3>
          <Badge variant="secondary" className="text-lg font-bold">
            {formatCost(estimate.estimatedCost)}
          </Badge>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between text-muted-foreground">
            <span className="font-medium">{t("cost.totalApiCalls")}</span>
            <span className="font-semibold text-foreground">{estimate.totalCalls}</span>
          </div>

          <div className="border-t pt-2 space-y-1.5">
            {estimate.breakdown.summaryGeneration > 0 && (
              <div className="flex items-center justify-between animate-fade-in hover-scale transition-all">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs">{t("cost.summaryGeneration")}</span>
                  {showTooltips && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">ניתוח וסיכום מקיף של כל הצ'אט באמצעות AI מתקדם</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{estimate.breakdown.summaryGeneration} {t("cost.call")}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatCost(estimate.costBreakdown.summary)})
                  </span>
                </div>
              </div>
            )}

            {estimate.breakdown.imageAnalysis > 0 && (
              <div className="flex items-center justify-between animate-fade-in hover-scale transition-all">
                <div className="flex items-center gap-2">
                  <Image className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs">{t("cost.imageAnalysis")}</span>
                  {showTooltips && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">ניתוח תמונות באמצעות AI, זיהוי תוכן וחילוץ טקסט</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{estimate.breakdown.imageAnalysis} {t("cost.calls")}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatCost(estimate.costBreakdown.images)})
                  </span>
                </div>
              </div>
            )}

            {estimate.breakdown.audioTranscription > 0 && (
              <div className="flex items-center justify-between animate-fade-in hover-scale transition-all">
                <div className="flex items-center gap-2">
                  <Mic className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs">{t("cost.audioTranscription")}</span>
                  {showTooltips && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">תמלול קבצי אודיו לטקסט באמצעות AI</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{estimate.breakdown.audioTranscription} {t("cost.calls")}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatCost(estimate.costBreakdown.audio)})
                  </span>
                </div>
              </div>
            )}

            {estimate.breakdown.videoAnalysis > 0 && (
              <div className="flex items-center justify-between animate-fade-in hover-scale transition-all">
                <div className="flex items-center gap-2">
                  <Video className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs">{videoCount} {t("cost.videoAnalysis") || "סרטונים"}</span>
                  {showTooltips && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">ניתוח סרטונים, זיהוי תוכן ואובייקטים</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{estimate.breakdown.videoAnalysis} {t("cost.calls")}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatCost(estimate.costBreakdown.video)})
                  </span>
                </div>
              </div>
            )}

            {estimate.breakdown.documentAnalysis > 0 && (
              <div className="flex items-center justify-between animate-fade-in hover-scale transition-all">
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs">{documentCount} {t("cost.documentAnalysis") || "מסמכים"}</span>
                  {showTooltips && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">ניתוח מסמכים, חילוץ תוכן וזיהוי מידע</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{estimate.breakdown.documentAnalysis} {t("cost.calls")}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatCost(estimate.costBreakdown.documents)})
                  </span>
                </div>
              </div>
            )}

            {estimate.breakdown.contactAnalysis > 0 && (
              <div className="flex items-center justify-between animate-fade-in hover-scale transition-all">
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs">{contactCount} {t("cost.contactAnalysis") || "אנשי קשר"}</span>
                  {showTooltips && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">ניתוח כרטיסי ביקור, חילוץ פרטי קשר ומידע</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium">{estimate.breakdown.contactAnalysis} {t("cost.calls")}</span>
                  <span className="text-xs text-muted-foreground">
                    ({formatCost(estimate.costBreakdown.contacts)})
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

      </CardContent>
    </Card>
  );
}
